// ============================================================================
//  KpiCard.tsx — NexusCorp ERP · UI Modernisation · Linear-quality
//  Accent border-left (not top) · Icon zone · Hover lift · Optional link
// ============================================================================
import type { ElementType } from 'react'
import { useNavigate } from 'react-router-dom'

export interface KpiCardProps {
  label: string
  value: string | number
  icon: ElementType
  color: string
  iconBg?: string
  sub?: string
  trend?: string
  trendUp?: boolean
  link?: string
}

export function KpiCard({ label, value, icon: Icon, color, iconBg, sub, trend, trendUp, link }: KpiCardProps) {
  const bg = iconBg ?? `${color}14`
  const navigate = useNavigate()

  return (
    <div
      onClick={link ? () => navigate(link) : undefined}
      className={`
        relative bg-white border border-[var(--bd)] rounded-xl px-4 py-4
        flex items-center gap-3.5 overflow-hidden
        transition-all duration-200 ease-out group
        hover:shadow-[0_4px_20px_rgba(15,12,29,0.08)] hover:-translate-y-px
        ${link ? 'cursor-pointer' : 'cursor-default'}
      `}
      style={{ borderLeft: `3px solid ${color}` }}
    >
      {/* Icon */}
      <div
        className="w-10 h-10 rounded-[10px] shrink-0 flex items-center justify-center transition-transform duration-200 group-hover:scale-105"
        style={{ background: bg, color }}
      >
        <Icon size={19} />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <p className="text-[22px] font-bold text-[var(--tx)] leading-none tracking-[-0.02em]">
          {value}
        </p>
        <p className="text-[11.5px] font-medium text-[var(--tx3)] mt-1.5 leading-tight truncate">
          {label}
        </p>
        {sub && (
          <p className="text-[10.5px] text-[var(--tx4)] mt-0.5 truncate">{sub}</p>
        )}
      </div>

      {/* Trend badge */}
      {trend && (
        <div className={`
          absolute top-3 right-3 text-[10px] font-semibold px-1.5 py-px rounded-full
          ${trendUp ? 'bg-[var(--green-bg)] text-[var(--green-text)]' : 'bg-[var(--red-bg)] text-[var(--red-text)]'}
        `}>
          {trendUp ? '↑' : '↓'} {trend}
        </div>
      )}
    </div>
  )
}
